import TruncatedTypography from "@/src/components/common/ui/truncatedTypography";
import useFormatDate from "@/src/hooks/date/useFormatDate";
import { FlightCard, FlightRouteInfo, JsonsData } from "@/src/types/flight";
import { getFlightLogoUrl } from "@/src/utils/generalUtils/airlineImage";
import { convertDDMMYYYYToISO } from "@/src/utils/date/convertDateFormat";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useFormatTime } from "@/src/hooks/srp/formatTime";
import CircleIcon from '@mui/icons-material/Circle';

interface FlightDetailsHeaderProps {
    sectorData?: FlightRouteInfo[];
    jsons?: JsonsData; // Airport metadata
    flight: FlightCard;
}

export const FlightDetailsCardHeader = ({ sectorData, jsons, flight }: FlightDetailsHeaderProps) => {
    const t = useTranslations("common");
    const tFareFamily = useTranslations("fareFamily");
    const { formatDate } = useFormatDate();

    const sectorInfo = sectorData?.map((sector) => {
        const firstSegment = sector?.flights?.segments[0] ?? {};
        const airlineName = firstSegment.flightName;
        const airlineLogo = getFlightLogoUrl(firstSegment.flightNumber.split("-")[0]);
        const formatTime = useFormatTime()
        const duration = formatTime(flight.totalDuration)
        return {
            stops: sector.stops === 0 ? t("direct") : tFareFamily("stops", { count: sector.stops }),
            logo: airlineLogo,
            airlineName: airlineName,
            firstAirportCode: sector.firstDeparture.airportCode,
            lastAirportCode: sector.lastArrival.airportCode,
            duration: duration
        };
    });

    if (!sectorInfo || sectorInfo.length === 0) return null;
    const firstSector = sectorInfo[0];
    const departureAirport = jsons?.ap?.[firstSector.firstAirportCode].c
    const arrivalAirport = jsons?.ap?.[firstSector.lastAirportCode].c

    return (
        <Box display="flex" alignItems="flex-start" gap={1} pb={1}>
            {/* Left: Logo & Airline */}
            <Stack direction="column" alignItems="center" spacing={0.5} mt={0}>
                <Box display="flex" gap={1} alignItems="center" justifyContent={"center"}>
                    <Box
                        sx={{
                            borderRadius: "10%",
                            width: 32,
                            height: 32,
                            fontSize: "14px",
                            color: "#808080",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 500,
                        }}
                    >
                        <Box
                            component="img"
                            src={firstSector.logo}
                            alt={firstSector.airlineName}
                            sx={{ width: 24, height: 24, objectFit: "contain" }}
                        />
                    </Box>
                </Box>
                <Box sx={{margin : '0'}}>
                <TruncatedTypography maxWidth={48} fontSize="10px" fontWeight={500} align="center">
                        {firstSector.airlineName}
                </TruncatedTypography>
                </Box>
            </Stack>

            {/* Right: Route Info */}
            <Stack direction="column" alignItems="flex-start">
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" fontWeight={600} fontSize="14px">
                        {departureAirport}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} fontSize="14px">
                        –
                    </Typography>
                    <Typography variant="caption" fontWeight={600} fontSize="14px">
                        {arrivalAirport}
                    </Typography>
                </Box>

                <Box sx={{ margin: '0', lineHeight: 1, alignItems: 'center', display: 'flex', gap: 1 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: "12px",
                        }}
                    >
                        {firstSector.stops}
                    </Typography>
                    <CircleIcon sx={{ fontSize: 5, color: 'grey.500' }} />
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: "12px",
                        }}
                    >
                        {firstSector.duration}
                    </Typography>
                </Box>
            </Stack>
        </Box>
    );
};